import Decimal from 'decimal.js';

/**
 * Money and price values cross the wire as decimal strings (see API
 * contract). They must never touch a JS `number` or `parseFloat` — both lose
 * precision silently. Every formatter here takes a string (or null/undefined
 * for "not available yet") and returns a string built from Decimal.
 */

export type DecimalInput = string | null | undefined;

export function parseMoney(value: DecimalInput): Decimal | null {
  if (value === null || value === undefined || value.trim() === '') return null;
  try {
    return new Decimal(value);
  } catch {
    return null;
  }
}

const GROUP_RE = /\B(?=(\d{3})+(?!\d))/g;

/**
 * Formats a decimal string for display with a fixed number of fraction
 * digits and thousands grouping, without ever going through a binary float.
 * `79564.55` renders as `79 564.55`, exactly — no rounding drift.
 */
export function formatMoney(value: DecimalInput, fractionDigits = 2): string {
  const decimal = parseMoney(value);
  if (decimal === null) return '—';
  const fixed = decimal.toFixed(fractionDigits);
  const negative = fixed.startsWith('-');
  const unsigned = negative ? fixed.slice(1) : fixed;
  const [whole, frac] = unsigned.split('.');
  const groupedWhole = (whole ?? '0').replace(GROUP_RE, ' ');
  const grouped = frac === undefined ? groupedWhole : `${groupedWhole}.${frac}`;
  return negative ? `-${grouped}` : grouped;
}

export function formatUsdt(value: DecimalInput, fractionDigits = 2): string {
  const formatted = formatMoney(value, fractionDigits);
  return formatted === '—' ? formatted : `${formatted} USDT`;
}

export function formatPrice(value: DecimalInput, fractionDigits = 1): string {
  return formatMoney(value, fractionDigits);
}

export function formatPercent(value: DecimalInput, fractionDigits = 2): string {
  const decimal = parseMoney(value);
  if (decimal === null) return '—';
  return `${decimal.toFixed(fractionDigits)}%`;
}

export function formatQuantity(value: DecimalInput, fractionDigits = 4): string {
  const decimal = parseMoney(value);
  if (decimal === null) return '—';
  const fixed = decimal.toFixed(fractionDigits);
  if (!fixed.includes('.')) return fixed;
  return fixed.replace(/0+$/, '').replace(/\.$/, '');
}

export function isNegative(value: DecimalInput): boolean {
  const decimal = parseMoney(value);
  return decimal !== null && decimal.isNegative();
}
